// Modal for creating a new channel or starting a DM. Picks members from the
// existing root.people directory. Calls createChannel which handles the
// insert + membership rows + dedupe.

export const ChatNewChannelModal = {
  extends: 'Modal',
  stateKey: 'showNewChannel',
  extraClose: { newChannelDraft: null },

  Window: {
    width: 'I',
    maxWidth: '90vw',
    ModalHeader: {
      Hgroup: {
        H: { text: (el, s) => s.root?.newChannelDraft?.kind === 'channel' ? 'Create channel' : 'New direct message' },
        P: {
          text: (el, s) => s.root?.newChannelDraft?.kind === 'channel'
            ? 'Pick a name and add the people who should join'
            : 'Pick one or more people to message'
        }
      }
    },

    Content: {
      flow: 'y',
      gap: 'A',
      flex: '1',
      overflow: 'auto',
      padding: 'A 0',

      KindToggle: {
        flow: 'x',
        gap: 'Z',

        ChannelBtn: {
          extends: 'ChatModalKindBtn',
          text: '# Channel',
          kind: 'channel',
          onClick: (e, el, s) => s.root.update({
            newChannelDraft: { ...s.root?.newChannelDraft, kind: 'channel' }
          }),
          theme: (el, s) => s.root?.newChannelDraft?.kind === 'channel' ? 'primary' : 'secondary'
        },
        DMBtn: {
          extends: 'ChatModalKindBtn',
          text: 'Direct',
          kind: 'dm',
          onClick: (e, el, s) => s.root.update({
            newChannelDraft: { ...s.root?.newChannelDraft, kind: 'dm' }
          }),
          theme: (el, s) => s.root?.newChannelDraft?.kind !== 'channel' ? 'primary' : 'secondary'
        }
      },

      NameField: {
        display: (el, s) => s.root?.newChannelDraft?.kind === 'channel' ? 'flex' : 'none',
        flow: 'y',
        gap: 'V',

        CaptionTitle: { fontSize: 'Y2', Text: { text: 'NAME' } },
        Input: {
          tag: 'input',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'document',
          color: 'title',
          fontSize: 'Z2',
          placeholder: 'e.g. design-reviews',
          value: (el, s) => s.root?.newChannelDraft?.name || '',
          onInput: (e, el, s) => s.root.update({
            newChannelDraft: { ...s.root?.newChannelDraft, name: e.target.value }
          })
        }
      },

      TopicField: {
        display: (el, s) => s.root?.newChannelDraft?.kind === 'channel' ? 'flex' : 'none',
        flow: 'y',
        gap: 'V',

        CaptionTitle: { fontSize: 'Y2', Text: { text: 'TOPIC (optional)' } },
        Input: {
          tag: 'input',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'document',
          color: 'title',
          fontSize: 'Z2',
          value: (el, s) => s.root?.newChannelDraft?.topic || '',
          onInput: (e, el, s) => s.root.update({
            newChannelDraft: { ...s.root?.newChannelDraft, topic: e.target.value }
          })
        }
      },

      MembersField: {
        flow: 'y',
        gap: 'V',
        maxHeight: 'G',
        overflow: 'hidden',

        HeaderRow: {
          flow: 'x',
          align: 'center space-between',

          CaptionTitle: { fontSize: 'Y2', Text: { text: 'MEMBERS' } },

          SelectAllBtn: {
            tag: 'button',
            type: 'button',
            fontSize: 'Y2',
            color: 'accent',
            background: 'transparent',
            borderWidth: '0',
            cursor: 'pointer',
            padding: '0',
            ':hover': { opacity: '.8' },
            text: (el, s) => {
              const me = s.root?.currentUserEmail
              const all = el.call('activeMembers').filter(p => p.email !== me)
              const selected = s.root?.newChannelDraft?.members || []
              return selected.length >= all.length ? 'Deselect all' : 'Select all'
            },
            onClick: (e, el, s) => {
              const me = s.root?.currentUserEmail
              const all = el.call('activeMembers').filter(p => p.email !== me)
              const selected = s.root?.newChannelDraft?.members || []
              const members = selected.length >= all.length ? [] : all.map(p => p.email)
              s.root.update({
                newChannelDraft: { ...s.root?.newChannelDraft, members }
              })
            }
          }
        },

        Search: {
          tag: 'input',
          padding: 'Z A',
          round: 'Z',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'document',
          color: 'title',
          fontSize: 'Z2',
          placeholder: 'Search teammates…',
          value: (el, s) => s.root?.newChannelDraft?.memberQuery || '',
          onInput: (e, el, s) => s.root.update({
            newChannelDraft: { ...s.root?.newChannelDraft, memberQuery: e.target.value }
          }),
          onKeydown: (e, el, s) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            const q = (s.root?.newChannelDraft?.memberQuery || '').trim().toLowerCase()
            if (!q) return
            const me = s.root.currentUserEmail
            const first = el.call('activeMembers').find(p => {
              if (p.email === me) return false
              const n = (p.name || '').toLowerCase()
              const em = (p.email || '').toLowerCase()
              return n.includes(q) || em.includes(q)
            })
            if (!first) return
            const draft = s.root?.newChannelDraft || { members: [] }
            const has = (draft.members || []).includes(first.email)
            const members = has
              ? draft.members.filter(m => m !== first.email)
              : [...(draft.members || []), first.email]
            s.root.update({
              newChannelDraft: { ...draft, members, memberQuery: '' }
            })
            e.target.value = ''
          }
        },

        List: {
          flow: 'y',
          gap: 'V',
          overflow: 'auto',
          maxHeight: 'F',
          childExtends: 'ChatMemberPickRow',
          childrenAs: 'state',
          children: (el, s) => {
            const me = s.root?.currentUserEmail
            const q = ((s.root?.newChannelDraft && s.root?.newChannelDraft.memberQuery) || '').trim().toLowerCase()
            const all = el.call('activeMembers').filter(p => p.email !== me)
            if (!q) return all
            return all.filter(p => {
              const n = (p.name || '').toLowerCase()
              const em = (p.email || '').toLowerCase()
              return n.includes(q) || em.includes(q)
            })
          }
        }
      }
    },

    ModalFooter: {
      flow: 'x',
      gap: 'Z1',

      SubmitBtn: {
        extends: 'Button',
        theme: 'primary',
        text: (el, s) => s.root?.newChannelDraft?.kind === 'channel' ? 'Create channel' : 'Open DM',
        onClick: async (e, el, s) => {
          const d = s.root?.newChannelDraft || {}
          const memberEmails = (d.members || []).filter(Boolean)
          const isChannel = d.kind === 'channel'
          // Block submit unless the draft is valid: a channel needs a name,
          // a DM needs at least one other member. Avoids creating empty
          // stub rows that show as blank entries in the sidebar.
          if (isChannel && !(d.name || '').trim()) return
          if (!isChannel && memberEmails.length === 0) return
          const kind = isChannel
            ? 'channel'
            : (memberEmails.length > 1 ? 'group_dm' : 'dm')
          await el.call('createChannel', {
            kind,
            name: isChannel ? d.name.trim() : null,
            topic: d.topic || null,
            isPrivate: !!d.is_private,
            memberEmails
          })
        }
      },
    }
  }
}

export const ChatModalKindBtn = {
  tag: 'button',
  padding: 'Z A',
  round: 'Z',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '600'
}

export const ChatMemberPickRow = {
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  padding: 'Z Z1',
  round: 'Z',
  cursor: 'pointer',
  ':hover': { background: 'overlay' },
  background: (el, s) => (s.root?.newChannelDraft?.members || []).includes(s.email) ? 'overlay' : 'transparent',

  onClick: (e, el, s) => {
    const draft = s.root?.newChannelDraft || { members: [] }
    const has = (draft.members || []).includes(s.email)
    const members = has
      ? draft.members.filter(m => m !== s.email)
      : [...(draft.members || []), s.email]
    s.root.update({ newChannelDraft: { ...draft, members } })
  },

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B B',
    src: (el, s) => el.call('avatarUrl', s && s.name)
  },
  Name: {
    tag: 'span',
    flex: '1',
    fontSize: 'Z2',
    color: 'title',
    text: (el, s) => s.name || s.email
  },
  Check: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'accent',
    text: (el, s) => (s.root?.newChannelDraft?.members || []).includes(s.email) ? '✓' : ''
  }
}
